import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, UserRound, X } from 'lucide-react';

/* A searchable client picker for the admin.

   It replaces the pattern this used to be: a text input that filtered a native
   `<select size={5}>` underneath it. That control looked like a raw OS widget,
   listed "Name (email)" as one cramped line each, told you nothing about who was
   selected once you clicked away, and needed two moves — type here, then click
   there — for a single decision.

   This is one field. Closed, it shows who is selected (avatar, name, email) or
   invites you to pick. Open, it is a search box over an avatar list you can
   drive entirely from the keyboard. Styles live in adminShell.css so every admin
   page gets the same picker; it expects an `.adminx` ancestor (AdminLayout). */

export type PickableUser = {
  uid?: string;
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

/* /auth/users/all returns either shape of id, so every caller ends up writing
   this — do it here once instead. */
export const userPickerId = (u: PickableUser) => u._id || u.uid || '';

const nameOf = (u: PickableUser) =>
  `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unnamed user';

const initialsOf = (u: PickableUser) => {
  const first = (u.firstName || '').trim()[0] || '';
  const last = (u.lastName || '').trim()[0] || '';
  return ((first + last) || (u.email || '?')[0] || '?').toUpperCase();
};

/* Every row is a search result, so show which part of it matched. */
function Marked({ text, q }: { text: string; q: string }) {
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{text.slice(at, at + q.length)}</mark>
      {text.slice(at + q.length)}
    </>
  );
}

/* A long client list doesn't need to be in the DOM all at once. Past this many
   matches the footer says so and asks for a narrower search, rather than
   quietly dropping the rest. */
const MAX_ROWS = 60;

type Props = {
  users: PickableUser[];
  /** Selected user id (`_id` or `uid`), or '' for none. */
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  placeholder?: string;
  /** Names the control for screen readers, since the visible label isn't tied to it. */
  ariaLabel?: string;
};

export default function UserPicker({
  users,
  value,
  onChange,
  loading = false,
  placeholder = 'Search or select a client…',
  ariaLabel = 'Select a client',
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = useMemo(
    () => users.find(u => userPickerId(u) === value),
    [users, value],
  );

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase().includes(needle),
    );
  }, [users, q]);

  const shown = matches.slice(0, MAX_ROWS);

  /* One exit path, so the popup never reopens holding a stale search. */
  const dismiss = () => { setOpen(false); setQ(''); };
  const close = () => { dismiss(); triggerRef.current?.focus(); };

  const pick = (u: PickableUser) => {
    onChange(userPickerId(u));
    close();
  };

  /* Opening lands the cursor on the current selection rather than back at the
     top. The query is cleared on close, not here, so this can't be undone by
     the `[q]` reset below firing on the same commit. */
  useEffect(() => {
    if (!open) return;
    const at = users.findIndex(u => userPickerId(u) === value);
    setActive(at < 0 ? 0 : Math.min(at, MAX_ROWS - 1));
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => { setActive(0); }, [q]);

  /* Keep the keyboard cursor in view without scrolling the page behind it. */
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) dismiss();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!shown.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive(i => (i + step + shown.length) % shown.length);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      if (!shown.length) return;
      setActive(e.key === 'Home' ? 0 : shown.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const u = shown[active];
      if (u) pick(u);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      dismiss();
    }
  };

  return (
    <div className={`upick${open ? ' open' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`upick-t${selected ? ' has' : ''}${selected ? ' clearable' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); }
        }}
      >
        {selected ? (
          <>
            <span className="upick-av">{initialsOf(selected)}</span>
            <span className="upick-txt">
              <span className="upick-n">{nameOf(selected)}</span>
              <span className="upick-e">{selected.email}</span>
            </span>
          </>
        ) : (
          <>
            <span className="upick-av ghost"><UserRound size={16} strokeWidth={2.2} /></span>
            <span className="upick-ph">{loading ? 'Loading clients…' : placeholder}</span>
          </>
        )}
        <ChevronDown className="upick-chev" size={16} strokeWidth={2.2} />
      </button>

      {/* Sibling, not a child of the trigger — a button inside a button is invalid. */}
      {selected && (
        <button
          type="button"
          className="upick-x"
          aria-label={`Clear ${nameOf(selected)}`}
          onClick={() => { onChange(''); triggerRef.current?.focus(); }}
        >
          <X size={14} strokeWidth={2.4} />
        </button>
      )}

      {open && (
        <div className="upick-pop">
          <div className="upick-q">
            <Search size={15} strokeWidth={2.2} />
            <input
              ref={inputRef}
              type="text"
              value={q}
              placeholder="Search by name or email…"
              autoComplete="off"
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={shown[active] ? `${listId}-o${active}` : undefined}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
            />
            {q && (
              <button
                type="button"
                className="upick-qx"
                aria-label="Clear search"
                onClick={() => { setQ(''); inputRef.current?.focus(); }}
              >
                <X size={13} strokeWidth={2.4} />
              </button>
            )}
          </div>

          {loading ? (
            <div className="upick-none"><div className="ax-spinner" /></div>
          ) : shown.length === 0 ? (
            <div className="upick-none">
              <b>No client found</b>
              <p>{q.trim() ? `Nothing matches “${q.trim()}”.` : 'No clients yet.'}</p>
            </div>
          ) : (
            <ul className="upick-list" id={listId} role="listbox" ref={listRef} aria-label={ariaLabel}>
              {shown.map((u, i) => {
                const id = userPickerId(u);
                const isOn = id === value;
                return (
                  <li
                    key={id || i}
                    id={`${listId}-o${i}`}
                    role="option"
                    aria-selected={isOn}
                    data-i={i}
                    className={`upick-o${i === active ? ' hot' : ''}${isOn ? ' on' : ''}`}
                    onMouseMove={() => setActive(i)}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => pick(u)}
                  >
                    <span className="upick-av">{initialsOf(u)}</span>
                    <span className="upick-txt">
                      <span className="upick-n"><Marked text={nameOf(u)} q={q.trim()} /></span>
                      <span className="upick-e"><Marked text={u.email || ''} q={q.trim()} /></span>
                    </span>
                    {isOn && <Check className="upick-ck" size={16} strokeWidth={2.6} />}
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && shown.length > 0 && (
            <div className="upick-foot">
              <span>
                {matches.length > MAX_ROWS
                  ? `Showing ${MAX_ROWS} of ${matches.length} — keep typing to narrow`
                  : `${matches.length} of ${users.length} client${users.length === 1 ? '' : 's'}`}
              </span>
              <span className="upick-keys"><kbd>↑</kbd><kbd>↓</kbd> move <kbd>↵</kbd> select</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
